/**
 * Typed client for the ADR-0202 r18 skill-manifest observations read.
 *
 *   GET /v1/sites/:site_id/manifest-observations
 *
 * Returns the recent observations for the active site newest-first.
 * The observed digest ships on the wire because the surface is
 * session-authenticated + RLS-scoped — the email/Slack digests
 * deliberately omit the digest (ADR-0202 §Decision 1).
 *
 * Wire format is snake_case (CLAUDE.md §3); mirror of every other
 * dashboard API client.
 */

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

/** One observation row on the wire. */
export interface ManifestObservationItem {
  readonly observation_id: string;
  readonly agent_id: string;
  readonly manifest_hash: string;
  readonly allowlisted: boolean;
  readonly first_seen_at: string;
  /**
   * `true` when this row represents a CHANGE observation (a new hash
   * for a (site, agent) pair whose prior baseline had already been
   * learned). `false` for a first-sight row that the ingest route
   * pre-stamped and never alerted on. The dashboard highlights only
   * the changes.
   */
  readonly change: boolean;
  /**
   * Total non-baseline observations for this (site, agent) across all
   * time, including dismissed rows. Used to surface "2nd manifest
   * change" context in the dashboard card.
   */
  readonly change_count: number;
  /**
   * The most recent baseline manifest hash for this agent before the
   * current change. Null for first-ever observations. Used to show
   * "Was: X → Now: Y" context in the card.
   */
  readonly previous_hash: string | null;
}

export type ListManifestObservationsResult =
  | { readonly kind: 'ok'; readonly items: readonly ManifestObservationItem[] }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

interface ListWire {
  readonly items?: readonly unknown[];
}

function parseItem(row: unknown): ManifestObservationItem | null {
  if (row === null || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const observation_id = r['observation_id'];
  const agent_id = r['agent_id'];
  const manifest_hash = r['manifest_hash'];
  const allowlisted = r['allowlisted'];
  const first_seen_at = r['first_seen_at'];
  const change = r['change'];
  const change_count_raw = r['change_count'];
  const previous_hash_raw = r['previous_hash'];
  const previous_hash = typeof previous_hash_raw === 'string' ? previous_hash_raw : null;
  if (
    typeof observation_id !== 'string'
    || typeof agent_id !== 'string'
    || typeof manifest_hash !== 'string'
    || typeof allowlisted !== 'boolean'
    || typeof first_seen_at !== 'string'
    || typeof change !== 'boolean'
  ) {
    return null;
  }
  const change_count =
    typeof change_count_raw === 'number'
      ? change_count_raw
      : typeof change_count_raw === 'string'
        ? Number(change_count_raw)
        : 1;
  return {
    observation_id,
    agent_id,
    manifest_hash,
    allowlisted,
    first_seen_at,
    change,
    change_count: Number.isFinite(change_count) && change_count > 0 ? change_count : 1,
    previous_hash,
  };
}

export async function dismissManifestObservation(
  http: HttpClient,
  site_id: string,
  observation_id: string,
): Promise<void> {
  try {
    await http.request<unknown>({
      method: 'PATCH',
      path: `/v1/sites/${encodeURIComponent(site_id)}/manifest-observations/${encodeURIComponent(observation_id)}/dismiss`,
    });
  } catch {
    // Best-effort — the card has already optimistically removed the row.
    // A network failure here is silent; the row reappears on next fetch.
  }
}

export async function listManifestObservations(
  http: HttpClient,
  site_id: string,
): Promise<ListManifestObservationsResult> {
  try {
    const res = await http.request<ListWire>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/manifest-observations`,
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const raw = Array.isArray(res.items) ? res.items : [];
    const items: ManifestObservationItem[] = [];
    for (const row of raw) {
      const parsed = parseItem(row);
      if (parsed !== null) items.push(parsed);
    }
    return { kind: 'ok', items };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: err.message };
    }
    if (err instanceof AuthRateLimitError) {
      return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}
